import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    ArrowLeftRight, Building2, Search, Package, Send, ChevronDown, Loader2,
    Trash2, X, RefreshCw,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchLevels,
    fetchFromCCList,
    fetchCategoryList,
    fetchGrid,
    fetchSuggestions,
    fetchDepPercent,
    addItem,
    deleteItem,
    submitTransfer,
    submitCSKTransfer,
    clearGrid,
    clearSuggestions,
    clearDepPercent,
    clearAddResult,
    clearSubmitResult,
    resetItemsTransfer,
    selectITIsCSK,
    selectITFromCCList,
    selectITCategoryList,
    selectITGridItems,
    selectITSuggestions,
    selectITDepPercentList,
    selectITAddResult,
    selectITSubmitResult,
    selectITLoading,
    selectITErrors,
} from '../../slices/purchaseSlice/itemsTransferSlice';

// ── Shared UI (mirrors DailyIssue / IndentAmend pages) ──────────────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
        purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

// CustomDatePicker's onChange fires with a Date object — format it before sending to the SP
const formatDateForSP = (d) => {
    if (!d) return '';
    if (d instanceof Date) {
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    }
    return String(d);
};

const SELECTION_TYPES = [
    { value: '1', label: 'Search By Item Code' },
    { value: '2', label: 'Search By Item Name' },
];

const DAYS_OPTIONS = ['1', '2', '3', '4', '5', '6', '7'];

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

// Items whose code starts with '1' are Assets — the legacy grid (Calculate()) blocks
// applying a depreciation percent to them at transfer time.
const isAssetCode = (code) => (code || '').trim().charAt(0) === '1';

const ItemsTransfer = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || '';
    const uid = userData?.UID || userData?.uid || '';
    const createdBy = userData?.userName || userData?.employeeId || uid || 'system';

    const isCSK        = useSelector(selectITIsCSK);
    const fromCCList    = useSelector(selectITFromCCList);
    const categoryList = useSelector(selectITCategoryList);
    const gridItems     = useSelector(selectITGridItems);
    const suggestions   = useSelector(selectITSuggestions);
    const depPercentList = useSelector(selectITDepPercentList);
    const addResult     = useSelector(selectITAddResult);
    const submitResult  = useSelector(selectITSubmitResult);
    const loading        = useSelector(selectITLoading);
    const errors          = useSelector(selectITErrors);

    const [selectedCC, setSelectedCC] = useState('');
    const [category, setCategory] = useState('');
    const [selectionType, setSelectionType] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchBoxRef = useRef(null);

    const [checkedRows, setCheckedRows] = useState({});
    const [qtyEdits, setQtyEdits] = useState({});
    const [stockTypeEdits, setStockTypeEdits] = useState({});
    const [depPercentEdits, setDepPercentEdits] = useState({});

    const [transferDate, setTransferDate] = useState(todayFormatted());
    const [days, setDays] = useState('');
    const [remarks, setRemarks] = useState('');

    // Resolve the caller's SK/CSK workflow level, and load static lookups, once on mount
    useEffect(() => {
        if (roleId) dispatch(fetchLevels(roleId));
        dispatch(fetchCategoryList());
        return () => dispatch(resetItemsTransfer());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (uid && roleId) dispatch(fetchFromCCList({ uid, rid: roleId }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid, roleId]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggestions(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    // Live autocomplete — fires 300ms after typing stops, matching the legacy jQuery UI widget
    useEffect(() => {
        if (!selectedCC || !category || !selectionType || !searchText.trim()) {
            dispatch(clearSuggestions());
            return;
        }
        const t = setTimeout(() => {
            dispatch(fetchSuggestions({ pfx: searchText.trim(), typ: selectionType, cat: category, cc: selectedCC }));
            setShowSuggestions(true);
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, selectionType, category, selectedCC]);

    // The legacy page replaces the whole grid's HTML on every refresh, which implicitly
    // discards any unsaved row edits — reproduce that by resetting our edit maps too.
    useEffect(() => {
        setCheckedRows({}); setQtyEdits({}); setStockTypeEdits({}); setDepPercentEdits({});
    }, [gridItems]);

    useEffect(() => {
        if (addResult) {
            toast.success(`${addResult.code} Added Successfully`);
            dispatch(clearAddResult());
            setSearchText('');
            setShowSuggestions(false);
            if (selectedCC) dispatch(fetchGrid({ cc: selectedCC, rid: roleId, isCSK }));
        } else if (errors.add) {
            toast.error(errors.add);
            dispatch(clearAddResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addResult, errors.add]);

    useEffect(() => {
        if (submitResult) {
            toast.success(`The Transfer No Is :- ${submitResult.refNo}   Generated Successfully`);
            dispatch(clearSubmitResult());
            handleReset();
        } else if (errors.submit) {
            toast.error(errors.submit);
            dispatch(clearSubmitResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitResult, errors.submit]);

    // ── Cost Center ──────────────────────────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setCategory(''); setSelectionType(''); setSearchText('');
        dispatch(clearSuggestions());
        dispatch(clearGrid());
        dispatch(clearDepPercent());
        if (cc) {
            dispatch(fetchGrid({ cc, rid: roleId, isCSK }));
            if (isCSK) dispatch(fetchDepPercent({ fromCC: cc }));
        }
    };

    // ── Item search ──────────────────────────────────────────────────────────

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setSelectionType('');
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleSelectionTypeChange = (e) => {
        if (!category) { toast.error('Select Search By Category'); return; }
        setSelectionType(e.target.value);
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handlePickSuggestion = (s) => {
        setSearchText(s.SearchItem || '');
        setShowSuggestions(false);
    };

    const handleClearSearch = () => {
        setSearchText('');
        dispatch(clearSuggestions());
    };

    const handleAddItem = () => {
        if (!category)      { toast.error('Select Search By Category'); return; }
        if (!selectionType) { toast.error('Select Search By Type'); return; }
        if (!searchText.trim()) { toast.error('Enter Search Data'); return; }
        if (!selectedCC)     { toast.error('Select Transfer Cost Center'); return; }

        const itemCode = searchText.split(',')[0].trim();
        dispatch(addItem({
            ItemCode: itemCode,
            FromCC: selectedCC,
            fullsearch: searchText,
            ItemCategoryCode: category,
        }));
    };

    // ── Grid row edits ───────────────────────────────────────────────────────

    const toggleRowChecked = (itId) => setCheckedRows((prev) => ({ ...prev, [itId]: !prev[itId] }));

    const handleQtyChange = (row, value) => {
        const avlQty = num(row.AvlQuantity);
        if (value !== '' && num(value) > avlQty) {
            toast.error('Invalid Quantity');
            setQtyEdits((prev) => ({ ...prev, [row.ItId]: '' }));
            setDepPercentEdits((prev) => ({ ...prev, [row.ItId]: '' }));
            return;
        }
        setQtyEdits((prev) => ({ ...prev, [row.ItId]: value }));
        if (value === '' || num(value) === 0) {
            setDepPercentEdits((prev) => ({ ...prev, [row.ItId]: '' }));
        }
    };

    const handleDepPercentChange = (row, value) => {
        if (isAssetCode(row.ItemCode) && num(value) !== 0) {
            toast.error('Depreciation Value is not applicable for Asset Items');
            return;
        }
        setDepPercentEdits((prev) => ({ ...prev, [row.ItId]: value }));
    };

    const beforeDepFor = (row) => {
        const qty = num(qtyEdits[row.ItId]);
        if (!qty) return null;
        return num(row.Basic) * qty;
    };

    const afterDepFor = (row) => {
        const before = beforeDepFor(row);
        if (before === null) return null;
        const dep = depPercentEdits[row.ItId];
        if (!dep || dep === '0') return null;
        if (num(dep) === 100) return 0;
        return before * num(dep) / 100;
    };

    const handleDeleteRow = (row) => {
        if (!checkedRows[row.ItId]) { toast.error('Please Check for Delete'); return; }
        dispatch(deleteItem({ ItId: row.ItId })).then((result) => {
            if (deleteItem.fulfilled.match(result)) {
                toast.success('Deleted Successfully');
                dispatch(fetchGrid({ cc: selectedCC, rid: roleId, isCSK }));
            } else {
                toast.error(result.payload || 'Failed to delete item');
            }
        });
    };

    // ── Totals (CSK only) ────────────────────────────────────────────────────

    const sumBeforeDep = useMemo(
        () => gridItems.reduce((s, r) => s + (beforeDepFor(r) || 0), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gridItems, qtyEdits]
    );
    const sumAfterDep = useMemo(
        () => gridItems.reduce((s, r) => s + (afterDepFor(r) || 0), 0),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [gridItems, qtyEdits, depPercentEdits]
    );

    // ── Submit ───────────────────────────────────────────────────────────────

    const handleSubmit = () => {
        if (!selectedCC)  { toast.error('Select Transfer Cost Center'); return; }
        if (!transferDate) { toast.error('Select Date'); return; }
        if (!days)          { toast.error('Select No of Days'); return; }
        if (!remarks.trim()) { toast.error('Select Remarks'); return; }
        if (gridItems.length === 0) { toast.error('Please Add Items to Transfer'); return; }

        if (!gridItems.every((r) => checkedRows[r.ItId])) {
            toast.error(isCSK ? 'Please Verify Transfer Item Codes' : 'Please Verify Item Codes');
            return;
        }
        if (!gridItems.every((r) => num(qtyEdits[r.ItId]) > 0)) {
            toast.error('Enter Quantity');
            return;
        }
        if (!gridItems.every((r) => stockTypeEdits[r.ItId] && stockTypeEdits[r.ItId] !== '0')) {
            toast.error('Select Items Status Type');
            return;
        }
        if (isCSK) {
            if (!gridItems.every((r) => (beforeDepFor(r) || 0) > 0)) {
                toast.error('Invalid Before Depriciation Amount');
                return;
            }
            if (!gridItems.every((r) => depPercentEdits[r.ItId] && depPercentEdits[r.ItId] !== '0')) {
                toast.error('Select Depriciation Percent');
                return;
            }
        }

        let ids = '', qtys = '', types = '';
        gridItems.forEach((r) => {
            ids   += `${r.ItId},`;
            qtys  += `${num(qtyEdits[r.ItId])},`;
            types += `${stockTypeEdits[r.ItId]},`;
        });

        const dateStr = formatDateForSP(transferDate);
        const basePayload = {
            ItId: ids,
            IssQuantity: qtys,
            StockType: types,
            FromCC: selectedCC,
            Date: dateStr,
            Remarks: remarks,
            Createdby: createdBy,
            Days: days,
            RoleID: String(roleId),
        };

        if (!isCSK) {
            dispatch(submitTransfer(basePayload));
            return;
        }

        // CSK flow — build the parallel Rowid/DepAmounts/Depcsk lists in lockstep, one
        // entry per staged row (the SP's WHILE loop consumes all three in the same
        // order). Depcsk === 100 ("Full Value") means no deduction: the full basic
        // value transfers and DepAmounts carries 0 for that row.
        let rowids = '', depcsk = '', depAmounts = '';
        let amountTotal = 0, effAmountTotal = 0;
        gridItems.forEach((r) => {
            const before = beforeDepFor(r) || 0;
            const dep = num(depPercentEdits[r.ItId]);
            rowids += `${r.ItId},`;
            if (dep !== 100) {
                const net = before - (before * dep / 100);
                depAmounts += `${net},`;
                depcsk += `${dep},`;
                amountTotal += net;
                effAmountTotal += (before * dep / 100);
            } else {
                depAmounts += `0,`;
                depcsk += `Full Value,`;
                amountTotal += before;
            }
        });

        dispatch(submitCSKTransfer({
            ...basePayload,
            Rowid: rowids,
            DepAmounts: depAmounts,
            EffAmount: effAmountTotal.toFixed(2),
            Depcsk: depcsk,
            Amount: amountTotal.toFixed(2),
        }));
    };

    const handleReset = () => {
        setSelectedCC(''); setCategory(''); setSelectionType(''); setSearchText('');
        setCheckedRows({}); setQtyEdits({}); setStockTypeEdits({}); setDepPercentEdits({});
        setTransferDate(todayFormatted()); setDays(''); setRemarks('');
        dispatch(clearGrid());
        dispatch(clearSuggestions());
        dispatch(clearDepPercent());
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const gridHasData = gridItems.length > 0;
    const checkedCount = gridItems.filter((r) => checkedRows[r.ItId]).length;

    if (loading.levels) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <ArrowLeftRight className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Items Transfer Issue</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Transfer stock items out of a cost center{isCSK ? ' — Central Store Keeper view' : ''}.
                        </p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── Cost Center selection ─────────────────────────────────────── */}
            <SectionCard icon={Building2} title="Cost Center" accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>Transfer Cost Center</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)} disabled={loading.fromCC}>
                                <option value="">-Please Select-</option>
                                {fromCCList.map((cc) => (
                                    <option key={cc.CCID} value={cc.CCID}>{cc.CCVAL}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.fromCC ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>
                    {loading.grid && (
                        <div className="flex items-center gap-2 text-sm text-indigo-600"><SpinnerIcon /> Loading items…</div>
                    )}
                </div>
            </SectionCard>

            {/* ── Item search ──────────────────────────────────────────────── */}
            {selectedCC && (
                <SectionCard icon={Search} title="Search &amp; Add Item" accent="amber">
                    <div className="flex items-end gap-3 flex-wrap">
                        <div className="min-w-[200px]">
                            <Label required>Search By Category</Label>
                            <select className={inputCls} value={category} onChange={handleCategoryChange} disabled={loading.categoryList}>
                                <option value="">-Please Select-</option>
                                {categoryList.map((c) => (
                                    <option key={c.Categoryid} value={c.Categoryid}>{c.CategoryValue}</option>
                                ))}
                            </select>
                        </div>
                        <div className="min-w-[200px]">
                            <Label required>Search By Type</Label>
                            <select className={inputCls} value={selectionType} onChange={handleSelectionTypeChange} disabled={!category}>
                                <option value="">-Please Select-</option>
                                {SELECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        <div className="relative flex-1 min-w-[260px]" ref={searchBoxRef}>
                            <Label required>Search Here</Label>
                            <input type="text" disabled={!selectionType} placeholder="Start typing to search…"
                                className={`${inputCls} uppercase`}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value.toUpperCase())}
                                onFocus={() => searchText && setShowSuggestions(true)} />
                            {loading.suggestions && (
                                <Loader2 className="absolute right-3 top-9 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                            )}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                                    {suggestions.map((s, i) => (
                                        <button key={i} type="button" onClick={() => handlePickSuggestion(s)}
                                            className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b border-gray-50 dark:border-gray-700 last:border-0">
                                            {s.SearchItem}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={handleAddItem} disabled={loading.add}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
                                {loading.add ? <SpinnerIcon /> : <Search className="w-4 h-4" />} Add
                            </button>
                            <button onClick={handleClearSearch}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                        </div>
                    </div>
                </SectionCard>
            )}

            {/* ── Staged items grid ────────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Package} title={isCSK ? 'Transfer Items' : 'Items Details'}
                    subtitle={`${checkedCount} of ${gridItems.length} item${gridItems.length !== 1 ? 's' : ''} verified`}
                    accent="indigo">
                    <div className="overflow-x-auto rounded-lg border border-indigo-200 dark:border-indigo-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white">
                                    {(isCSK
                                        ? ['Chk', 'Item Code', 'Item Name', 'Specification', 'DCA', 'Sub DCA', 'Units', 'Basic', 'Available Qty', 'Quantity', 'Item Status', 'Before Dep', 'Depreciation', 'After Dep', '']
                                        : ['Chk', 'Item Code', 'Item Name', 'Specification', 'DCA', 'Sub DCA', 'Units', 'Item Status', 'Available Qty', 'Quantity', '']
                                    ).map((h) => (
                                        <th key={h} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {gridItems.map((row, idx) => {
                                    const asset = isAssetCode(row.ItemCode);
                                    const before = beforeDepFor(row);
                                    const after = afterDepFor(row);
                                    return (
                                        <tr key={row.ItId} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-indigo-50/40 dark:bg-indigo-900/10'}>
                                            <td className="px-2.5 py-2">
                                                <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                    checked={!!checkedRows[row.ItId]} onChange={() => toggleRowChecked(row.ItId)} />
                                            </td>
                                            <td className="px-2.5 py-2 whitespace-nowrap font-mono text-gray-800 dark:text-gray-200">{row.ItemCode}</td>
                                            <td className="px-2.5 py-2 text-gray-900 dark:text-white">{row.ItemName}</td>
                                            <td className="px-2.5 py-2 text-gray-600 dark:text-gray-400">{row.Specification}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.DcaCode}</td>
                                            <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.SubDcaCode}</td>
                                            <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300">{row.Units}</td>

                                            {isCSK && (
                                                <td className="px-2.5 py-2 text-right text-gray-700 dark:text-gray-300">{num(row.Basic).toFixed(2)}</td>
                                            )}

                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{row.AvlQuantity}</td>

                                            <td className="px-1.5 py-1.5 w-24">
                                                <input type="number" min="0" step="0.01"
                                                    className={`${inputCls} text-right`}
                                                    value={qtyEdits[row.ItId] ?? ''}
                                                    onChange={(e) => handleQtyChange(row, e.target.value)} />
                                            </td>

                                            {!isCSK && (
                                                <td className="px-1.5 py-1.5 w-28">
                                                    <select className={inputCls}
                                                        value={stockTypeEdits[row.ItId] || '0'}
                                                        onChange={(e) => setStockTypeEdits((p) => ({ ...p, [row.ItId]: e.target.value }))}>
                                                        <option value="0">Select</option>
                                                        <option value="Stock">Stock</option>
                                                        <option value="NewStock">NewStock</option>
                                                    </select>
                                                </td>
                                            )}

                                            {isCSK && (
                                                <>
                                                    <td className="px-1.5 py-1.5 w-28">
                                                        <select className={inputCls}
                                                            value={stockTypeEdits[row.ItId] || '0'}
                                                            onChange={(e) => setStockTypeEdits((p) => ({ ...p, [row.ItId]: e.target.value }))}>
                                                            <option value="0">Select</option>
                                                            <option value="Stock">Stock</option>
                                                            <option value="NewStock">NewStock</option>
                                                        </select>
                                                    </td>
                                                    <td className="px-2.5 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                                        {before !== null ? before.toFixed(2) : '—'}
                                                    </td>
                                                    <td className="px-1.5 py-1.5 w-32">
                                                        <select className={inputCls}
                                                            value={depPercentEdits[row.ItId] || '0'}
                                                            disabled={!num(qtyEdits[row.ItId]) || asset}
                                                            onChange={(e) => handleDepPercentChange(row, e.target.value)}>
                                                            <option value="0">{asset ? 'N/A (Asset)' : 'Select'}</option>
                                                            {!asset && depPercentList.map((p) => (
                                                                <option key={p.Value} value={p.Value}>{p.Name}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-2.5 py-2 text-right text-green-700 dark:text-green-400 font-semibold whitespace-nowrap">
                                                        {after !== null ? after.toFixed(2) : '—'}
                                                    </td>
                                                </>
                                            )}

                                            <td className="px-1.5 py-1.5">
                                                <button onClick={() => handleDeleteRow(row)} disabled={loading.deleteItem}
                                                    className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 disabled:opacity-50">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            {isCSK && (
                                <tfoot>
                                    <tr className="bg-indigo-50/60 dark:bg-indigo-900/20 font-semibold text-gray-700 dark:text-gray-200">
                                        <td colSpan={10} className="px-2.5 py-2 text-right">Total</td>
                                        <td className="px-2.5 py-2 text-right text-green-700 dark:text-green-400">{sumBeforeDep.toFixed(2)}</td>
                                        <td></td>
                                        <td className="px-2.5 py-2 text-right text-green-700 dark:text-green-400">{sumAfterDep.toFixed(2)}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label required>Transfer Date</Label>
                            <CustomDatePicker value={transferDate} onChange={(d) => setTransferDate(formatDateForSP(d))}
                                format="DD-MMM-YYYY" placeholder="Select Date" maxDate={new Date()} />
                        </div>
                        <div>
                            <Label required>No of Days</Label>
                            <select className={inputCls} value={days} onChange={(e) => setDays(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {DAYS_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter remarks…"
                                value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button onClick={handleReset}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold">
                            Reset
                        </button>
                        <button onClick={handleSubmit} disabled={loading.submit}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                            {loading.submit ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                            {loading.submit ? 'Submitting…' : 'Submit'}
                        </button>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default ItemsTransfer;
